// ShellClient — the ONLY thing the React UI talks to (KSE-0185 phase 3).
//
// The shared Swift core runs inside a dedicated module Worker
// (src/core/worker.ts, which hosts instantiate() + the OPFS FileBackend). This
// file speaks the worker RPC from shell-types.ts: calls go out as
// {id, type:'call', name, args}, replies come back as result/error, and the
// core pushes MailShellSnapshot values as unsolicited {type:'snapshot'}
// messages. The UI renders the latest snapshot and dispatches ShellCommands.

import { coreWasmUrl } from "virtual:lately-swift-core";
import type { ReactNode } from "react";
import { createContext, createElement, useContext, useSyncExternalStore } from "react";

import {
  type CalendarShellSnapshot,
  EMPTY_SNAPSHOT,
  type MailShellSnapshot,
  SHELL_RPC,
  type ShellCallMessage,
  type ShellCommand,
  type ShellCommandResult,
  type ShellOkResult,
  type ShellOpenConfig,
  type ShellOpenResult,
  type ShellRpcName,
  type ShellStorageReason,
  type ShellStorageState,
  type ShellTakeoverResult,
  type ShellWorkerInitConfig,
  type ShellWorkerInitResult,
  type ShellWorkerOutbound,
  type ShellWorkerStorage,
} from "./shell-types";

// The subset of Worker the client needs — a real Worker satisfies it, and so
// does the in-process MockShellWorker used when no core is available.
export interface ShellWorkerLike {
  postMessage(message: unknown): void;
  addEventListener(type: "message", listener: (event: MessageEvent) => void): void;
  addEventListener(type: "error", listener: (event: ErrorEvent) => void): void;
  removeEventListener(type: "message", listener: (event: MessageEvent) => void): void;
  removeEventListener(type: "error", listener: (event: ErrorEvent) => void): void;
  terminate(): void;
}

export type ShellEngine = "swift-shell" | "mock-shell";

export type SnapshotListener = (snapshot: MailShellSnapshot) => void;
export type CalendarSnapshotListener = (snapshot: CalendarShellSnapshot | null) => void;
export type StorageListener = (state: ShellStorageState) => void;

interface PendingCall {
  resolve: (value: unknown) => void;
  reject: (reason: Error) => void;
}

export class ShellClient {
  readonly engine: ShellEngine;
  // Resolves once the worker has spoken (a `ready` message or the first reply);
  // rejects if the worker script fails to start. createShellClient() awaits the
  // worker's `init` round trip before handing the client out, so by the time
  // the UI sees a client this has normally settled.
  readonly ready: Promise<void>;
  abiVersion: number | null = null;

  private readonly worker: ShellWorkerLike;
  private readonly pending = new Map<number, PendingCall>();
  private readonly snapshotListeners = new Set<SnapshotListener>();
  private readonly calendarListeners = new Set<CalendarSnapshotListener>();
  private readonly storageListeners = new Set<StorageListener>();
  private nextId = 1;
  private latest: MailShellSnapshot = EMPTY_SNAPSHOT;
  private latestCalendar: CalendarShellSnapshot | null = null;
  private snapshotCount = 0;
  private storageState: ShellStorageState | null = null;
  private terminated = false;
  private settleReady!: { resolve: () => void; reject: (reason: Error) => void };

  constructor(worker: ShellWorkerLike, engine: ShellEngine = "swift-shell") {
    this.worker = worker;
    this.engine = engine;
    this.ready = new Promise<void>((resolve, reject) => {
      this.settleReady = { resolve, reject };
    });
    // Swallow the rejection unless a caller awaits `ready` (open() surfaces
    // startup failures on its own).
    this.ready.catch(() => {});
    worker.addEventListener("message", this.onMessage);
    worker.addEventListener("error", this.onError);
  }

  // Latest snapshot pushed by the core; EMPTY_SNAPSHOT before the first one.
  get snapshot(): MailShellSnapshot {
    return this.latest;
  }

  // True once at least one snapshot has arrived — "the shell is live".
  get hasSnapshot(): boolean {
    return this.snapshotCount > 0;
  }

  // Latest CalendarShellSnapshot pushed by the core's calendar shell
  // (KSE-0191); null until the first one (no account, or the core predates
  // calendars). Stable identity between pushes (useSyncExternalStore).
  get calendarSnapshot(): CalendarShellSnapshot | null {
    return this.latestCalendar;
  }

  // Storage the worker is on ("opfs" or "memory"), initially from `init`,
  // then tracking the worker's unsolicited storage messages; null for the
  // mock engine or a worker without an init step.
  get storage(): ShellWorkerStorage | null {
    return this.storageState?.storage ?? null;
  }

  // Why the worker is on memory storage (see ShellStorageReason); null when
  // on OPFS, when memory was requested, or when unknown.
  get storageReason(): ShellStorageReason | null {
    return this.storageState?.reason ?? null;
  }

  // Both of the above as one value (stable identity between changes, so it
  // suits useSyncExternalStore).
  get storageSnapshot(): ShellStorageState | null {
    return this.storageState;
  }

  // Worker bootstrap: hands the served core URL to the worker, which fetches
  // and instantiates the wasm there (with the OPFS backend when available).
  // Must precede open(); createShellClient() does this. A worker that
  // bootstraps itself (no `init` handler) is tolerated.
  async init(config: ShellWorkerInitConfig): Promise<ShellWorkerInitResult | null> {
    try {
      const result = await this.call<ShellWorkerInitResult>(SHELL_RPC.init, [config]);
      this.applyStorage(result);
      this.abiVersion = typeof result.abiVersion === "number" ? result.abiVersion : null;
      return result;
    } catch (cause) {
      if (cause instanceof Error && /unknown worker call/i.test(cause.message)) {
        return null;
      }
      throw cause;
    }
  }

  // Closes the session and the core, then deletes the worker's OPFS
  // directory so nothing of the mailbox remains on disk. Called by sign-out
  // when the last account goes. The worker comes back up on a fresh pool, so
  // a later open() works. Resolves {ok:true} even for the mock.
  wipe(): Promise<ShellOkResult> {
    return this.call<ShellOkResult>(SHELL_RPC.wipe, []);
  }

  // Steals the storage lock from the tab that has it and re-initializes the
  // worker on OPFS. Any open session is gone afterwards — the caller opens
  // it again. Resolves with the storage the worker ended up on.
  async takeover(): Promise<ShellStorageState> {
    const result = await this.call<ShellTakeoverResult>(SHELL_RPC.takeover, []);
    if (result && typeof result === "object" && typeof result.storage === "string") {
      this.applyStorage(result);
    }
    return this.storageState ?? { storage: "memory" };
  }

  onStorage(listener: StorageListener): () => void {
    this.storageListeners.add(listener);
    return () => {
      this.storageListeners.delete(listener);
    };
  }

  open(config: ShellOpenConfig): Promise<ShellOpenResult> {
    // A new session starts from a clean slate: the previous account's last
    // snapshot must not render (or count as "live") while the core opens.
    this.latest = EMPTY_SNAPSHOT;
    this.snapshotCount = 0;
    this.notifySnapshot(EMPTY_SNAPSHOT);
    this.latestCalendar = null;
    this.notifyCalendarSnapshot(null);
    return this.call<ShellOpenResult>(SHELL_RPC.open, [config]);
  }

  command<C extends ShellCommand>(cmd: C): Promise<ShellCommandResult<C>> {
    return this.call<ShellCommandResult<C>>(SHELL_RPC.command, [cmd]);
  }

  close(): Promise<ShellOkResult> {
    return this.call<ShellOkResult>(SHELL_RPC.close, []);
  }

  onSnapshot(listener: SnapshotListener): () => void {
    this.snapshotListeners.add(listener);
    return () => {
      this.snapshotListeners.delete(listener);
    };
  }

  onCalendarSnapshot(listener: CalendarSnapshotListener): () => void {
    this.calendarListeners.add(listener);
    return () => {
      this.calendarListeners.delete(listener);
    };
  }

  // Tears down the worker; pending calls reject.
  terminate() {
    if (this.terminated) {
      return;
    }
    this.terminated = true;
    this.worker.removeEventListener("message", this.onMessage);
    this.worker.removeEventListener("error", this.onError);
    this.worker.terminate();
    this.failAll(new Error("Shell worker terminated."));
  }

  private call<T>(name: ShellRpcName, args: unknown[]): Promise<T> {
    if (this.terminated) {
      return Promise.reject(new Error("Shell worker terminated."));
    }
    const id = this.nextId++;
    const message: ShellCallMessage = { id, type: "call", name, args };
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (value: unknown) => void, reject });
      this.worker.postMessage(message);
    });
  }

  private notifySnapshot(snapshot: MailShellSnapshot) {
    for (const listener of this.snapshotListeners) {
      try {
        listener(snapshot);
      } catch (cause) {
        console.error("Lately: snapshot listener failed", cause);
      }
    }
  }

  private notifyCalendarSnapshot(snapshot: CalendarShellSnapshot | null) {
    for (const listener of this.calendarListeners) {
      try {
        listener(snapshot);
      } catch (cause) {
        console.error("Lately: calendar snapshot listener failed", cause);
      }
    }
  }

  // Records the worker's storage mode; listeners hear about it only when the
  // mode or the reason actually changed (init results and takeover replies
  // arrive alongside the worker's own storage messages).
  private applyStorage(state: ShellStorageState) {
    const next: ShellStorageState = state.reason
      ? { storage: state.storage, reason: state.reason }
      : { storage: state.storage };
    const previous = this.storageState;
    if (previous && previous.storage === next.storage && previous.reason === next.reason) {
      return;
    }
    this.storageState = next;
    for (const listener of this.storageListeners) {
      try {
        listener(next);
      } catch (cause) {
        console.error("Lately: storage listener failed", cause);
      }
    }
  }

  private readonly onMessage = (event: MessageEvent) => {
    const data = event.data as ShellWorkerOutbound | null;
    if (!data || typeof data !== "object" || typeof data.type !== "string") {
      return;
    }
    switch (data.type) {
      case "snapshot": {
        this.settleReady.resolve();
        this.latest = data.value;
        this.snapshotCount += 1;
        this.notifySnapshot(data.value);
        break;
      }
      case "calendarSnapshot": {
        this.settleReady.resolve();
        this.latestCalendar = data.value;
        this.notifyCalendarSnapshot(data.value);
        break;
      }
      case "result": {
        this.settleReady.resolve();
        const pending = this.pending.get(data.id);
        if (pending) {
          this.pending.delete(data.id);
          pending.resolve(data.value);
        }
        break;
      }
      case "error": {
        this.settleReady.resolve();
        const pending = this.pending.get(data.id);
        if (pending) {
          this.pending.delete(data.id);
          pending.reject(new Error(data.message));
        }
        break;
      }
      case "storage": {
        if (data.value && typeof data.value === "object") {
          this.applyStorage(data.value);
        }
        break;
      }
      case "ready":
        this.settleReady.resolve();
        break;
      default:
        break;
    }
  };

  private readonly onError = (event: ErrorEvent) => {
    const error = new Error(event.message || "Shell worker failed to start.");
    this.settleReady.reject(error);
    this.failAll(error);
  };

  private failAll(error: Error) {
    for (const pending of this.pending.values()) {
      pending.reject(error);
    }
    this.pending.clear();
  }
}

// ---------------------------------------------------------------------------
// Engine selection

// `import.meta.glob` (instead of `new Worker(new URL("./worker.ts",
// import.meta.url))`) so the app still builds and runs — on the mock engine —
// when src/core/worker.ts is absent; once it exists Vite bundles it as a
// module worker exactly like the literal form would.
const workerModules = import.meta.glob("./worker.ts", {
  query: "?worker",
  import: "default",
}) as Partial<Record<string, () => Promise<new () => Worker>>>;

export type EngineOverride = "swift-shell" | "mock-shell" | null;

// Explicit override for development: `?engine=mock` in the URL, or
// localStorage `lately.web.engine`. Anything else picks the real core when a
// worker is bundled and falls back to the mock otherwise.
export function readEngineOverride(): EngineOverride {
  if (typeof window === "undefined") {
    return null;
  }
  const fromQuery = new URLSearchParams(window.location.search).get("engine");
  const fromStorage = (() => {
    try {
      return localStorage.getItem("lately.web.engine");
    } catch {
      return null;
    }
  })();
  const raw = (fromQuery ?? fromStorage ?? "").toLowerCase();
  if (raw === "mock" || raw === "mock-shell") {
    return "mock-shell";
  }
  if (raw === "swift" || raw === "swift-shell") {
    return "swift-shell";
  }
  return null;
}

// Storage override for development: `?storage=memory` in the URL, or
// localStorage `lately.web.storage`. Default lets the worker pick OPFS.
export function readStorageOverride(): ShellWorkerStorage | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  const fromQuery = new URLSearchParams(window.location.search).get("storage");
  const fromStorage = (() => {
    try {
      return localStorage.getItem("lately.web.storage");
    } catch {
      return null;
    }
  })();
  const raw = (fromQuery ?? fromStorage ?? "").toLowerCase();
  return raw === "memory" || raw === "opfs" ? raw : undefined;
}

// The real engine needs both halves: the worker script (owned by the OPFS
// track) and a bundled core (LATELY_SWIFT_WASM_CORE_DIR at build time, which
// makes `coreWasmUrl` non-null). Anything less runs the mock so the UI still
// works — the header badge says which engine is live.
export async function createShellClient(
  override: EngineOverride = readEngineOverride(),
  signal?: AbortSignal,
) {
  const loadWorker = workerModules["./worker.ts"];
  const wasmUrl = typeof coreWasmUrl === "string" && coreWasmUrl.length > 0 ? coreWasmUrl : null;
  if (override !== "mock-shell" && loadWorker && wasmUrl) {
    const WorkerCtor = await loadWorker();
    if (signal?.aborted) {
      throw new DOMException("createShellClient aborted", "AbortError");
    }
    const client = new ShellClient(new WorkerCtor(), "swift-shell");
    // Let the caller tear the worker down while init is still pending
    // (React StrictMode mounts twice; the first worker must release the OPFS
    // pool before the second can take it).
    signal?.addEventListener("abort", () => client.terminate(), { once: true });
    try {
      const init = await client.init({ coreWasmUrl: wasmUrl, storage: readStorageOverride() });
      console.info(
        `Lately engine: shared Swift core in a worker (abi ${init?.abiVersion ?? "?"}, storage ${init?.storage ?? "?"})`,
      );
    } catch (cause) {
      client.terminate();
      throw cause;
    }
    return client;
  }
  // The mock is a development convenience only. A production build without
  // the Swift core is a broken build, not a reason to serve fixture mail.
  if (override === "swift-shell" || (!import.meta.env.DEV && override !== "mock-shell")) {
    throw new Error(
      loadWorker
        ? "The Swift core is not bundled in this build (LATELY_SWIFT_WASM_CORE_DIR was unset)."
        : "The Swift shell worker (src/core/worker.ts) is not bundled in this build.",
    );
  }
  if (!loadWorker) {
    console.warn("Lately: src/core/worker.ts is not bundled; running the mock shell engine.");
  } else if (!wasmUrl) {
    console.warn(
      "Lately: no Swift core bundled (LATELY_SWIFT_WASM_CORE_DIR); running the mock shell engine.",
    );
  }
  const { MockShellWorker } = await import("./mock-worker");
  return new ShellClient(new MockShellWorker(), "mock-shell");
}

// ---------------------------------------------------------------------------
// React bindings

const ShellContext = createContext<ShellClient | null>(null);

export function ShellProvider({ client, children }: { client: ShellClient; children?: ReactNode }) {
  return createElement(ShellContext.Provider, { value: client }, children);
}

export function useShell(): ShellClient {
  const client = useContext(ShellContext);
  if (!client) {
    throw new Error("useShell requires a ShellProvider ancestor");
  }
  return client;
}

// Subscribes the component to the latest MailShellSnapshot.
export function useShellSnapshot(client?: ShellClient): MailShellSnapshot {
  const fromContext = useContext(ShellContext);
  const target = client ?? fromContext;
  if (!target) {
    throw new Error("useShellSnapshot requires a ShellProvider ancestor or an explicit client");
  }
  return useSyncExternalStore(
    (onChange) => target.onSnapshot(() => onChange()),
    () => target.snapshot,
    () => target.snapshot,
  );
}

// Subscribes the component to the latest CalendarShellSnapshot (null until
// the core's calendar shell has published one).
export function useCalendarSnapshot(client?: ShellClient): CalendarShellSnapshot | null {
  const fromContext = useContext(ShellContext);
  const target = client ?? fromContext;
  if (!target) {
    throw new Error("useCalendarSnapshot requires a ShellProvider ancestor or an explicit client");
  }
  return useSyncExternalStore(
    (onChange) => target.onCalendarSnapshot(() => onChange()),
    () => target.calendarSnapshot,
    () => target.calendarSnapshot,
  );
}

// Subscribes the component to the worker's storage mode (opfs/memory + reason).
export function useShellStorage(client?: ShellClient): ShellStorageState | null {
  const fromContext = useContext(ShellContext);
  const target = client ?? fromContext;
  if (!target) {
    throw new Error("useShellStorage requires a ShellProvider ancestor or an explicit client");
  }
  return useSyncExternalStore(
    (onChange) => target.onStorage(() => onChange()),
    () => target.storageSnapshot,
    () => target.storageSnapshot,
  );
}
